import {
	Component,
	ElementRef,
	EventEmitter,
	Input,
	OnInit,
	Output,
	ViewChild,
	ViewEncapsulation,
	forwardRef,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { CKEditorComponent, CKEditorModule } from '@ckeditor/ckeditor5-angular';
import { DecoupledEditor, Mention, EditorConfig, Emoji, Essentials, Paragraph, Bold, Italic, AutoLink, FontBackgroundColor, FontColor, FontSize, ImageUpload, Indent, IndentBlock, Link, List, RemoveFormat, SpecialCharacters, SpecialCharactersEssentials, Underline, Undo } from 'ckeditor5';

import { BaseControlValueAccessor } from 'rev-shared/util/BaseControlValueAccessor';
import { getLocale } from 'rev-shared/bootstrap/LocaleContext';

import styles from './htmlEditor.module.less';

import 'ckeditor5/ckeditor5.css';
import 'ckeditor5-premium-features/ckeditor5-premium-features.css';
import { loadEmoji } from './EmojiData';

const CSS_FOCUS_CLASS: string = 'has-focus,theme-accent-border-important';

/**
 * wysiswyg html editor
 * Example:
 * <vb-ui-html-editor
		[(ngModel)]="output">
	</vb-ui-html-editor>
 */
@Component({
	selector: 'vb-ui-html-editor',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => VbUiHtmlEditorComponent),
			multi: true
		}
	],
	imports: [
		CommonModule,
		CKEditorModule,
		FormsModule
	],
	encapsulation: ViewEncapsulation.None,
	standalone: true,
	template: `
	<ckeditor [editor]="Editor"
		[config]="config"
		[ngClass]="[
			singleLineMode ? styles.singleLine : '',
		]"
		[(ngModel)]="value"
		(ngModelChange)="modelChange()"
		(ready)="editorReady()"
		(focus)="updateFocusClass(true)"
		(blur)="updateFocusClass(false)"
		#ckeditor>
	</ckeditor>
	<div [ngClass]="themedToolbarMode ? styles.themedToolbar : ''"
		#editorToolbarContainer>
	</div>
	`
})
export class VbUiHtmlEditorComponent extends BaseControlValueAccessor<string> implements OnInit {
	@Input() public editorConfig: EditorConfig;
	@Input() public singleLineMode: boolean; //If set, Enter key will cause submit() to fire
	@Input() public allowEnter: boolean;
	@Input() public autoFocus: boolean = false;
	@Input() public themedToolbarMode: boolean;
	@Input() public placeholder: string;
	@Output() public submit = new EventEmitter<void>();
	@Output() public editorLoaded = new EventEmitter<any>();

	@ViewChild('ckeditor') public editorComponent: CKEditorComponent;
	@ViewChild('editorToolbarContainer') public toolbarContainer: ElementRef;

	public config: any;
	public Editor = DecoupledEditor;
	public defaultConfig: EditorConfig = {
		licenseKey: 'eyJhbGciOiJFUzI1NiJ9.eyJleHAiOjE4NDM3NzU5OTksImp0aSI6IjMzZTAxOTZlLTkzNDQtNGJkOC04NjYzLWNhYTBjNWZiMTc1NCIsImRpc3RyaWJ1dGlvbkNoYW5uZWwiOlsic2giLCJkcnVwYWwiXSwid2hpdGVMYWJlbCI6dHJ1ZSwiZmVhdHVyZXMiOlsiRFJVUCIsIkRPIiwiRlAiLCJTQyIsIlRPQyIsIlRQTCIsIlBPRSIsIkNDIiwiTUYiLCJTRUUiLCJFQ0giLCJFSVMiXSwidmMiOiI0OGRkNmM0ZiJ9.mzbEKvBBanfol-NBX4SCc9eZmJQYZ0pJPxgO8z4d26cl5Jxs5cdWuVEitbt5FtEyTyOwN-hkhudONCpoB7m4zg',
		plugins: [
			AutoLink,
			Bold,
			Emoji,
			Essentials,
			FontBackgroundColor,
			FontColor,
			Mention,
			FontSize,
			ImageUpload,
			Indent,
			IndentBlock,
			Italic,
			Link,
			List,
			Paragraph,
			RemoveFormat,
			SpecialCharacters,
			SpecialCharactersEssentials,
			Underline,
			Undo
		],
		toolbar: {
			items: [
				'fontSize',
				'|',
				'bold',
				'italic',
				'underline',
				'removeFormat',
				'|',
				'emoji',
				'|',
				'bulletedList',
				'numberedList',
				'outdent',
				'indent',
				'|',
				'fontColor',
				'fontBackgroundColor',
				'|',
				'link',
				'|',
				'undo',
				'redo'
			],
			shouldNotGroupWhenFull: true
		},
		emoji: {
			definitionsUrl: 'cdn'
		}
	};
	public styles = styles;

	private div: HTMLDivElement;

	constructor(
		private readonly element: ElementRef,
	) {
		super();
	}

	public modelChange(): void {
		this.updateModelValue(this.value);
	}

	public ngOnInit(): void {
		this.config = this.getConfig();
	}

	public editorReady(): void {
		// Editor instance is not available immediately (even on ready 🤬), so we need to wait a bit
		setTimeout(() => {
			this.editorLoaded.emit(this.editorComponent.editor);
			this.div = this.element.nativeElement.querySelector('div');
			const editor = this.editorComponent.editorInstance;
			const toolbarContainer = this.toolbarContainer?.nativeElement;
			const toolbarElement = editor?.ui?.view?.toolbar?.element;
			if (toolbarContainer && toolbarElement) {
				toolbarContainer.appendChild(toolbarElement);
			}
			if (editor) {
				loadEmoji(editor.plugins.get('EmojiRepository'));
				if (this.autoFocus) {
					this.focusEditor(editor);
				}
				if (this.singleLineMode && !this.allowEnter) {
					editor.editing.view.document.on('enter', (evt, data) => {
						data.preventDefault();
						evt.stop();
						this.updateModelValue(this.value);
						this.submit.emit();
					});
				}
			}

		}, 100);
	}

	private focusEditor(editor: any): void {
		const editableElement = editor?.ui?.view?.editable?.element as HTMLElement;
		editableElement?.focus();
		editor?.editing?.view?.focus?.();
	}

	public getConfig(): any {
		return {
			language: getLocale().ckEditorLocale,
			link: {
				addTargetToExternalLinks: true
			},
			...this.defaultConfig,
			...this.editorConfig,
			placeholder: this.placeholder || ''
		};
	}

	public updateFocusClass(isAdded: boolean): void {
		CSS_FOCUS_CLASS.split(',').forEach(className => this.div.classList.toggle(className, isAdded));
	}
}
